// AUDIENCE · 시청자 분포 — OAuth 연동 크리에이터의 인구통계 스냅샷 (성별·연령·상위 국가).
//
// 소스: 포털서버 Mongo ChannelDemographicsSnapshot (크리에이터 대시보드 §2.6과 동일 데이터).
// 커버리지 원칙(스펙 §7-1): 데이터 있으면 노출, 미연동/무데이터(null)면 **섹션 자체 생략** — 빈 칸 금지.
// 인구통계는 사실 자체가 중립이라 그대로 노출(긍정 프레임과 충돌 없음). 스냅샷 날짜를 note로 고지.
import type { AudienceDemographics } from '../../creatorsApi'
import { InfoTip } from '../InfoTip'
import { SectionHeader } from './SectionHeader'

const GENDER_LABEL: Record<string, string> = {
  female: '여성',
  male: '남성',
  genderUserSpecified: '기타',
}

/** "age25-34" → "25–34", "age65-" → "65+". 미지 키는 verbatim(가짜 라벨 금지). */
function ageLabel(key: string): string {
  const m = key.match(/^age(\d+)(?:-(\d+))?-?$/)
  if (!m) return key
  return m[2] ? `${m[1]}–${m[2]}` : `${m[1]}+`
}

/** ISO 국가코드 → 한글 국가명 (Intl 내장 — 하드코딩 금지). 실패 시 코드 verbatim. */
function countryLabel(code: string): string {
  try {
    return new Intl.DisplayNames(['ko'], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

function pct(v: number): string {
  return `${v.toFixed(1)}%`
}

/** 라벨 + 수치 + 수평 게이지 한 줄. 게이지는 사각(radius 0), 트랙 soft / 채움 brand-blue. */
function DistRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="py-[5px]">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate font-text text-[12px] text-fg-3">{label}</span>
        <span className="flex-none font-display text-[14px] font-bold tabular-nums text-ink-800">
          {pct(value)}
        </span>
      </div>
      <div className="mt-[5px] h-[5px] bg-surface-soft" aria-hidden>
        <div
          className="h-full bg-brand-blue"
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      </div>
    </div>
  )
}

export function AudienceDemographicsPanel({ data }: { data: AudienceDemographics | null }) {
  // null(미연동/무데이터/조회 실패) 또는 전 분포 결측 → 섹션 생략
  if (!data) return null
  const hasAny = data.gender.length > 0 || data.ageGroup.length > 0 || data.country.length > 0
  if (!hasAny) return null

  const snapshotDate = data.capturedAt ? data.capturedAt.slice(0, 10) : null

  return (
    <section className="mt-[34px]">
      <SectionHeader
        label="AUDIENCE · 시청자 분포"
        info={
          <InfoTip
            label="시청자 분포 집계 기준"
            text="연동된 YouTube 채널의 시청자 통계(YouTube Analytics)입니다. 채널을 연동한 크리에이터만 표시됩니다."
          />
        }
        note={snapshotDate ? `Snapshot · ${snapshotDate}` : undefined}
      />
      <div className="grid gap-px border border-line-hairline bg-line-hairline md:grid-cols-3">
        {data.gender.length > 0 && (
          <div className="min-w-0 bg-surface-base p-[20px_22px]">
            <div className="font-text text-[11px] tracking-[0.04em] text-fg-3">성별 / GENDER</div>
            <div className="mt-2">
              {data.gender.map((g) => (
                <DistRow key={g.key} label={GENDER_LABEL[g.key] ?? g.key} value={g.viewerPercentage} />
              ))}
            </div>
          </div>
        )}
        {data.ageGroup.length > 0 && (
          <div className="min-w-0 bg-surface-base p-[20px_22px]">
            <div className="font-text text-[11px] tracking-[0.04em] text-fg-3">연령대 / AGE</div>
            <div className="mt-2">
              {data.ageGroup.map((a) => (
                <DistRow key={a.key} label={ageLabel(a.key)} value={a.viewerPercentage} />
              ))}
            </div>
          </div>
        )}
        {data.country.length > 0 && (
          <div className="min-w-0 bg-surface-base p-[20px_22px]">
            <div className="font-text text-[11px] tracking-[0.04em] text-fg-3">상위 국가 / TOP GEO</div>
            <div className="mt-2">
              {data.country.map((c) => (
                <DistRow key={c.key} label={countryLabel(c.key)} value={c.viewerPercentage} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
